import { IconWrapper, getTokens } from '@common/Entity/EntityIcons';
import { HUB_URL } from '@common/packageHub/constants';
import { useProjectSubscriberTeams } from '@common/projectHooks';
import { QueryKeys } from '@common/queries/common';
import { updateProject } from '@common/queries/project';
import { PotentialSubscribedTeamQuery, addSubscriberToProjectQuery } from '@common/queries/team';
import { Entity } from '@common/types/Entity';
import { EntityEvents } from '@common/user-analytics';
import { useIsUserTeamAdmin } from '@common/useUserType';
import { SyncMode, SyncModeType } from '@prophecy/graphql-types/enums';
import { ShoppingBagAIcon, DotsHorizontalIcon } from '@prophecy/icons';
import { ExtractArrayType } from '@prophecy/interfaces/generic';
import { BaseColor, theme } from '@prophecy/theme';
import {
  Avatar,
  Button,
  Dropdown,
  Ellipsis,
  Input,
  Select,
  Stack,
  StackItem,
  Switch,
  Table,
  TableActions,
  TableIconButton,
  Text,
  Tooltip
} from '@prophecy/ui-v3';
import { Alert, toast } from '@prophecy/ui-v3/ui-compat';
import { useQueryClient } from '@tanstack/react-query';
import { ResultOf } from 'gql.tada';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useGlobalVariables } from '../../../../../context/globalConfig/securedConfigHook';
import { useMixPanel } from '../../../../../context/mixpanel/context';
import { useGraphQlMutation, useGraphQlQuery } from '../../../../../data/util';
import { RevokeDialog } from './RevokeDialog';

const StyledSelect = styled(Select)`
  flex-grow: 1;
`;
const TableWrapper = styled(StackItem)`
  min-height: 225px;
`;
const StyledInput = styled(Input)`
  flex-grow: 1;
`;
type Team = ExtractArrayType<
  NonNullable<ResultOf<typeof PotentialSubscribedTeamQuery>['Project']>['potentialSubscriberTeams']
>;
const Card = styled(Stack)<{ radius: string }>`
  flex-shrink: 0;
  border-radius: ${({ radius }) => radius};
  border: 1px solid ${theme.colors.gray200};
  background: ${theme.colors.white};
`;
const PackageHubButton = styled(Button)`
  padding: 0;
  height: auto;
` as typeof Button;

const shoppingIconTokens = getTokens(BaseColor.orange);
const nonAdminUserTooltip = "Only Team's admin can perform this action";
const sqlProjectPublishedTooltip = 'Sql projects cannot be published';
export function ShareWithTeam({
  projectName,
  teamId,
  isSqlProject,
  projectId,
  syncMode,
  latestAttemptedReleaseVersion,
  publishedToPackageHub
}: {
  teamId: string;
  syncMode: SyncModeType;
  isSqlProject: boolean;
  projectName: string;
  projectId: string;
  latestAttemptedReleaseVersion?: string;
  publishedToPackageHub: boolean;
}) {
  const isTeamAdmin = useIsUserTeamAdmin(teamId);
  const isProphecyManagedGit = syncMode === SyncMode.Internal;
  const isReleased = Boolean(latestAttemptedReleaseVersion);
  const disabled = !isTeamAdmin ? true : !isReleased || (isProphecyManagedGit && isSqlProject);

  let disabledMessage = '';
  if (isProphecyManagedGit && isSqlProject) {
    disabledMessage = 'Prophecy managed sql projects cannot be shared';
  } else if (!isReleased) {
    disabledMessage = 'Please release this project to enable sharing';
  }
  const packageHubTooltip = !isTeamAdmin ? nonAdminUserTooltip : isSqlProject ? sqlProjectPublishedTooltip : undefined;
  const [teamToRevoke, setTeamToRevoke] = useState<Team>();
  const [selectedTeamName, setTeamName] = useState('');
  const globalConf = useGlobalVariables();
  const isGlobalAccessEnabled = globalConf.isGlobalAccessEnabled;
  const variables = { uid: projectId };
  const { data, isInitialLoading: isPotentialSubscribedTeamLoading } = useGraphQlQuery(
    [QueryKeys.Project, 'PotentialSubscribedTeam', variables],
    PotentialSubscribedTeamQuery,
    undefined,
    variables
  );
  const unsubscribedTeams = data?.Project?.potentialSubscriberTeams || [];
  const { data: subscriberTeamsData, isInitialLoading: isSubscriberTeamsLoading } =
    useProjectSubscriberTeams(projectId);
  const subscribedTeams = subscriberTeamsData?.Project?.subscriberTeams || [];

  const { track } = useMixPanel();

  useEffect(() => {
    track(EntityEvents[Entity.Project].metadata.publishOpen, { projectId, entity: Entity.Project });
  }, [track, projectId]);

  const { mutateAsync, isLoading } = useGraphQlMutation(addSubscriberToProjectQuery);
  const { mutateAsync: updateProjectMutateAsync, isLoading: updateProjectIsLoading } =
    useGraphQlMutation(updateProject);
  const queryClient = useQueryClient();
  async function publishToPackageHub(checked: boolean) {
    if (publishedToPackageHub !== checked) {
      updateProjectMutateAsync(
        {
          uid: projectId,
          publishedProphecyPackage: checked
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([QueryKeys.Git, QueryKeys.Project]);
            toast.success({
              content: checked
                ? 'Project published to Package Hub successfully.'
                : 'Project unpublished from Package Hub successfully.'
            });
          }
        }
      );
    }
  }
  const handleSubmit = async () => {
    const teamName = selectedTeamName.trim();
    let teamId = teamName;
    if (!isGlobalAccessEnabled) {
      const _teamId = unsubscribedTeams.find((team) => team.name === teamName)?._id;

      if (subscribedTeams.some((team) => team.name === teamName)) {
        toast.error({ content: `Team ${teamName} is already subscribed.` });
        return;
      } else if (!_teamId) {
        toast.error({ content: `Team ${teamName} does not exist.` });
        return false;
      }

      teamId = _teamId;
    }
    await mutateAsync(
      {
        uid: projectId,
        teamId: teamId
      },
      {
        onSuccess: (resp) => {
          if (resp?.addSubscriberToProject === 1) {
            queryClient.invalidateQueries([Entity.Project]);
            track(EntityEvents[Entity.Project].metadata.publishSuccess, { teamId: teamId, projectId: projectId });
            toast.success({ content: 'Published successfully' });
          }
          setTeamName('');
        }
      }
    );
  };
  const revokeDialog = teamToRevoke ? (
    <RevokeDialog
      teamName={teamToRevoke.name}
      teamId={teamToRevoke._id}
      uid={projectId}
      onClose={() => setTeamToRevoke(undefined)}
    />
  ) : null;
  const packageHubSwitch = (
    <Switch
      checked={publishedToPackageHub}
      onChange={publishToPackageHub}
      disabled={disabled || updateProjectIsLoading}
      data-test-id='share-with-team-package-hub-switch'
      aria-label='Publish to Package Hub'
    />
  );
  return (
    <>
      {revokeDialog}
      <Stack height='100%' gap={theme.spaces.x32}>
        {disabledMessage ? <Alert closable={false}>{disabledMessage}</Alert> : null}
        <Stack gap={theme.spaces.x12}>
          <Text level='lg' weight={theme.fontWeight.medium}>
            Share "{projectName}" with a Team
          </Text>
          <Text level='sm'>
            You can easily share this project with any Team, even if you are not a member. When you share a project with
            a Team, it becomes available in read-only mode to all Team members and their associated projects.
          </Text>
        </Stack>
        <Card
          radius={theme.radius.x8}
          direction='horizontal'
          gap={theme.spaces.x16}
          padding={theme.spaces.x24}
          alignY='end'>
          {isGlobalAccessEnabled ? (
            <StyledSelect
              loading={isPotentialSubscribedTeamLoading}
              label='Team(s)*'
              showSearch={true}
              options={unsubscribedTeams.map((team) => ({ value: team._id, label: team.name }))}
              onChange={setTeamName}
              value={selectedTeamName}
              data-test-id='share-with-team-team-select'
            />
          ) : (
            <StyledInput
              label='Team(s)*'
              value={selectedTeamName}
              onChange={setTeamName}
              addOn={
                <Text level='sm' tone={theme.colors.gray900}>
                  Read-only
                </Text>
              }
              addOnPlacement='right'
              data-test-id='share-with-team-team-input'
            />
          )}
          <Button
            tooltip={!isTeamAdmin ? nonAdminUserTooltip : undefined}
            disabled={disabled}
            loading={isLoading}
            onClick={handleSubmit}
            data-test-id='share-with-team-send-invitation-button'>
            Send Invitation
          </Button>
        </Card>
        <TableWrapper grow='1'>
          <Table
            height='100%'
            virtualize={true}
            loading={isSubscriberTeamsLoading}
            rowKey='_id'
            data-test-id='share-with-team-subscribed-teams-table'
            dataSource={subscribedTeams}
            columns={[
              {
                title: 'Team',
                dataIndex: 'name',
                width: '60%',
                key: 'name',
                filter: {
                  type: 'search',
                  searchInputProps: {
                    placeholder: 'Search Team',
                    'data-test-id': 'share-with-team-team-search-input'
                  }
                },
                sortable: true,
                render: (value: string) => {
                  return (
                    <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
                      <span>
                        <Avatar withBorder={true} size='xs' text={value} />
                      </span>
                      <StackItem grow='1'>
                        <Text level='sm13' tone={theme.colors.gray900}>
                          <Ellipsis tooltip={true}>{value}</Ellipsis>
                        </Text>
                      </StackItem>
                    </Stack>
                  );
                }
              },
              {
                title: 'Access',
                width: '20%',
                dataIndex: 'isAuthor',
                key: 'isAuthor',
                render: (value) => {
                  return <Text level='sm'>{value ? 'Owner' : 'Read-only'}</Text>;
                }
              },
              {
                width: '20%',
                render: (value, record) => {
                  if (!isTeamAdmin) {
                    return null;
                  }
                  return (
                    <TableActions gap={theme.spaces.x12} align='end'>
                      <Dropdown
                        align='end'
                        data-test-id='share-with-team-dropdown'
                        overlay={
                          <>
                            <Dropdown.Item
                              onClick={() => setTeamToRevoke(record as Team)}
                              data-test-id='revoke-access-dropdown-item'>
                              Revoke Access
                            </Dropdown.Item>
                          </>
                        }>
                        <TableIconButton
                          icon={<DotsHorizontalIcon type='default' size='xs' />}
                          data-test-id='share-with-team-revoke-access-button'
                          aria-label='Revoke Share with Team access'
                        />
                      </Dropdown>
                    </TableActions>
                  );
                }
              }
            ]}></Table>
        </TableWrapper>
        <Card radius={theme.radius.x16} direction='horizontal' align='space-between' padding={theme.spaces.x24}>
          <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
            <IconWrapper
              $radius={theme.radius.x6}
              align='center'
              padding={theme.spaces.x12}
              alignY='center'
              $background={shoppingIconTokens.backgroundType.default.background}
              $border={shoppingIconTokens.backgroundType.default.border}>
              <ShoppingBagAIcon type='default' size='l' color={shoppingIconTokens.backgroundType.default.color} />
            </IconWrapper>

            <Stack gap={theme.spaces.x6}>
              <Text tone={theme.colors.gray900} weight={theme.fontWeight.medium} level='md'>
                Share this Project with the Community
              </Text>
              <Text tone={theme.colors.gray600} level='xs'>
                Publish this project to the{' '}
                <PackageHubButton
                  elementType='link'
                  variant='link'
                  to={HUB_URL.PACKAGE}
                  data-test-id='share-with-team-package-hub-button'>
                  Package Hub
                </PackageHubButton>{' '}
                and contribute to a collaborative data-driven community.
              </Text>
            </Stack>
          </Stack>
          <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
            <Text level='sm' weight={theme.fontWeight.medium} tone={theme.colors.gray700}>
              Publish to Package Hub
            </Text>
            {isTeamAdmin && !isSqlProject ? (
              packageHubSwitch
            ) : (
              <Tooltip title={packageHubTooltip}>
                <span>{packageHubSwitch}</span>
              </Tooltip>
            )}
          </Stack>
        </Card>
      </Stack>
    </>
  );
}
